import React, { useEffect, useState } from "react";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import { PolicyFullObject, PolicyValuesObject } from "../../types/insurance.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import PolicyInstallmentsDataGrid from "../DataTables/PolicyInstallmentsDataGrid.tsx";

interface PolicyDetailsData {
  policy: PolicyFullObject | null;
  policyValues: PolicyValuesObject[];
}

export const PolicyDetails: React.FC<{ policyId: string }> = ({ policyId }) => {
  const { jwt } = useAuth();
  const [data, setData] = useState<PolicyDetailsData | null>(null);
  const [agents, setAgents] = useState<[]>([]);
  const [payPlaces, setPayPlaces] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchGenericData = async () => {
      const [agentsRes, payPlacesRes] = await Promise.all([
        InsuranceService.getAllAgents(jwt),
        PolicyService.fetchPayPlaces(jwt),
      ]);

      setAgents(agentsRes as []);
      setPayPlaces(payPlacesRes);
    };

    const getPolicyDetailsByPolicyId = async () => {
      try {
        setIsLoading(true);
        const policyDetails = await PolicyService.fetchPolicyDetailsByPolicyId(
          policyId,
          jwt
        );
        setData(policyDetails);
      } catch (err) {
        console.error("Error loading policy details:", err);
      } finally {
        setIsLoading(false);
      }
    };

    getPolicyDetailsByPolicyId();
    fetchGenericData();
  }, [policyId, jwt]);

  const policy = data?.policy || null;
  const policyValues = Array.isArray(data?.policyValues)
    ? data.policyValues
    : [];

  const itemDates = policyValues
    .map((v) => v.installmentDate)
    .filter(Boolean)
    .sort();

  const computedFromDate = itemDates[0] || "NoDate";
  const computedToDate = itemDates[itemDates.length - 1] || "NoDate";

  const renderValue = (key: string, value: any) => {
    if (key === "agentId") return agents?.[value] || value || "-";
    if (key === "paymentLocation") return payPlaces?.[value] || value || "-";
    return value || "-";
  };

  return (
    <div className="mt-4 space-y-4">
      {/* Top Section: Structured Core Details Map */}
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <div className="border-stroke dark:border-strokedark border-b px-4 py-2">
          <h3 className="text-sm font-bold text-black dark:text-white">
            Данни за полица
          </h3>
        </div>
        {policy &&
        Object.keys(policy).length > 0 &&
        !Object.values(policy).every((value) => !value) ? (
          <div className="grid grid-cols-2 gap-2 p-4 text-xs text-black dark:text-white md:grid-cols-3 lg:grid-cols-4">
            {Object.entries(policy).map(([key, value]) => (
              <div key={key} className="truncate">
                <span className="font-semibold text-gray-700 dark:text-gray-400">
                  {translatePolicyKey(key)}:{" "}
                </span>
                <span className="text-gray-800 dark:text-gray-200">
                  {renderValue(key, value) || "-"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="px-4 py-2 text-xs text-gray-500">
            Няма данни за полица
          </p>
        )}
      </div>

      {/* Bottom Section: Custom Sub-Grid Integration */}
      <PolicyInstallmentsDataGrid
        installments={policyValues}
        isLoading={isLoading}
        fromDate={computedFromDate}
        toDate={computedToDate}
        renderValueFn={renderValue}
      />
    </div>
  );
};

export function translatePolicyKey(key: string) {
  const map: Record<string, string> = {
    policyNumber: "№ Полица",
    insuranceCompany: "Застраховател",
    insuranceType: "Вид застраховка",
    insurancePremium: "Премия",
    contractType: "№ вноска",
    agentPaymentStatus: "Пл. към агент",
    commissionAgentPercentage: "Ком. Агент %",
    commissionOfficePercentage: "Ком. Офис %",
    commissionOfficeAmount: "Ком. Офис сума",
    commissionAgentAmount: "Ком. Агент сума",
    stickerNumber: "№ Стикер",
    stickerAmount: "Сума стикер",
    twoPercentAmount: "2%",
    twoPercentSum: "2%",
    claimReference: "СЗК",
    paymentLocation: "Място плащане",
    accountNumber: "№ Сметка",
    policyType: "Вид полица",
    agentId: "Агент",
    agent: "Агент",
    divisionName: "Офис",
    fromDate: "От дата",
    toDate: "До дата",
    installments: "№",
    clientName: "Клиент",
    clientPhoneNumber: "Тел. номер",
    installmentDate: "Падеж дата",
    installmentSequence: "Пор. №",
    division: "Офис",
    policyStatus: "Статус",
    insuredSum: "Застр. сума",
    policySum: "Сума",
    gfSum: "ГФ сума",
    contractDate: "Дата договор",
    startDate: "Начало",
    endDate: "Край",
    reportDate: "Дата отчет",
    installmentsCount: "Брой вноски",
    policyAdditionalData: "Доп. инфо",
    vehicleRegistrationNumber: "Рег. №",
    vehicleCertificate: "Свидетелство",
    vehicleBrand: "Марка",
    engineCapacity: "Кубатура",
    manufactureYear: "Година",
    insuranceClientName: "Клиент",
    insuranceClientAddress: "Адрес клиент",
    insuranceClientPhone: "Тел. клиент",
    zeroCommission: "0% Ком.",
    placeOfPayment: "Място плащане",
    amountWithTax: "Сума",
    taxAmount: "2%",
    orderNumber: "№ Падеж",
    stickerSum: "Сума стикер",
    totalSum: "Сума",
    insuranceFundSum: "ГФ Сума",
    PolNum: "Номер",
    InsComp: "ЗК",
    DocType: "Тип",
    DateR: "Дата",
    office: "Офис",
    policiesSum: "Сума полици",
    commissionPercent: "Комисионна %",
    commissionAmount: "Комисион",
    paymentPlace: "Място плащане",
    company: "ЗК",
    phoneNumber: "Тел №",
    registrationNumber: "Рег №",
    installmentAmount: "Сума вноска",
    installmentNumber: "№",
    paymentDate: "Дата",
    year: "Година",
    brand: "Марка",
    enginePower: "Мощност",
    documentNumber: "№ документ",
    status: "Статус",
    sticker: "Стикер",
    greenCardCertificate: "СЗК",
    paidToAgent: "Платено - агент",
    paidInBank: "Платено - банка",
    bankPaymentDate: "Дата плащане банка",
    clientPIN: "ЕГН/ЕИК",
    greenCardNumber: "СЗК",
    twoPercent: "2%",
    additionalPolicyData: "Доп. Инфо",
    insuredIdentifier: "Застрахован",
    insuredAddress: "Адрес",
    personalId: "ЕГН",
    insuranceClientPin: "ЕГН",
    agentCommissionPercentage: "Комисионна агент",
  };
  return map[key] || key;
}
